import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type {
  AnchorState,
  Ticket,
  TicketStatus,
  TicketType,
} from "../api/types";
import { AlertTriangle, Link2, ListChecks, X } from "../compose/Icon";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { initials } from "../lib/format";
import {
  anchorIsFlagged,
  anchorStateLabel,
  formatAge,
  statusLabel,
  STATUS_SEGMENTS,
  typeLabel,
  TYPE_SEGMENTS,
} from "../lib/tickets";
import { ticketTypeIcon } from "../lib/ticketIcons";
import { useAsync } from "../lib/useAsync";
import { KanbanBoard } from "./kanban/KanbanBoard";
import { commentBodyPreview } from "../lib/commentPreview";
import { buildCitationPath, CITATION_PARAM } from "../lib/permalink";

/** The tickets queue's two views: the article-grouped LIST (default) and the
 *  status KANBAN board. Persisted URL-first (`?view=`) then localStorage. */
type TicketsView = "list" | "board";
const VIEW_STORAGE_KEY = "ela.tickets.view";
const VIEW_SEGMENTS: SegmentedOption<TicketsView>[] = [
  { value: "list", label: "List" },
  { value: "board", label: "Board" },
];

function readStoredView(): TicketsView | null {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === "board" || v === "list" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Tickets queue `/w/:slug/tickets` (design screens §6, ia-and-flows §5 + the
 * article-first IA sweep §5): the workspace-level cross-document review index,
 * now ARTICLE-UNIT-GROUPED. Every review comment across every document,
 * filterable by TYPE + STATUS (both URL-synced so a view is
 * shareable/bookmarkable) and scopable to one article-unit via `?unit=<id>` (the
 * workbench "N tickets" deep-link). Rows that belong to an article-unit nest
 * under that unit's header and drill into the workbench pane
 * (`/w/:slug/article/:unitId?show=<role>&comment=<id>`); loose/legacy docs keep
 * the flat "Other documents" trailer and the `/doc/:id?comment=<id>` link —
 * nothing regresses. Orphaned anchors stay first-class rows with their quote preserved
 * (brief §7). This is the new-hire's work queue (brief §2.6).
 *
 * Composes the elaborate-owned `TicketsTable`/`TicketRow` over `SegmentedControl`
 * (web-segmented-control) + `states` (web-states/skeleton) + the `role-chip`
 * chrome lifted from the Articles landing — no re-solved primitives.
 */

/** Human label for a resource role chip (design §1.2). */
function roleLabel(role: string): string {
  switch (role) {
    case "brief":
      return "Brief";
    case "research":
      return "Research";
    case "article":
      return "Article";
    default:
      return role;
  }
}

/**
 * A queue section: either an article-unit (`unitId` set) with its resources'
 * tickets nested, or the loose "Other documents" trailer (`unitId === null`).
 * Kept local (design §5 parallel-independence rule); the integrator may promote
 * `groupByUnit` to a shared `lib/articleGroups.ts` for Proposals/Activity reuse.
 */
interface UnitGroup {
  unitId: number | null;
  title: string;
  slug: string | null;
  tickets: Ticket[];
}

/** Bin tickets by `article_unit_id`, preserving the API's created_at-desc order
 *  (Map keeps first-seen unit order); loose-doc tickets fall to a trailer. */
export function groupByUnit(tickets: Ticket[]): UnitGroup[] {
  const byUnit = new Map<number, UnitGroup>();
  const loose: Ticket[] = [];
  for (const t of tickets) {
    if (t.article_unit_id != null) {
      let g = byUnit.get(t.article_unit_id);
      if (!g) {
        g = {
          unitId: t.article_unit_id,
          title: t.unit_title ?? "Untitled unit",
          slug: t.unit_slug ?? null,
          tickets: [],
        };
        byUnit.set(t.article_unit_id, g);
      }
      g.tickets.push(t);
    } else {
      loose.push(t);
    }
  }
  const groups: UnitGroup[] = [...byUnit.values()];
  if (loose.length > 0) {
    groups.push({
      unitId: null,
      title: "Other documents",
      slug: null,
      tickets: loose,
    });
  }
  return groups;
}

export function Tickets() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();

  const type = (params.get("type") ?? "all") as TicketType | "all";
  const status = (params.get("status") ?? "all") as TicketStatus | "all";
  const unitParam = params.get("unit");
  const unit = unitParam ? Number(unitParam) : undefined;
  const unitScoped = unit != null && Number.isFinite(unit);
  const filtered = type !== "all" || status !== "all" || unitScoped;

  // View: URL `?view=` primary → localStorage → default `list` (design §4.4).
  const viewParam = params.get("view");
  const view: TicketsView =
    viewParam === "board" || viewParam === "list"
      ? viewParam
      : (readStoredView() ?? "list");

  const tickets = useAsync(
    () =>
      api.listTickets(slug, {
        type: type === "all" ? undefined : type,
        status: status === "all" ? undefined : status,
        unit: unitScoped ? unit : undefined,
      }),
    [slug, type, status, unitScoped ? unit : undefined],
  );

  const setFilter = (key: "type" | "status", value: string) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const clearUnit = () => {
    const next = new URLSearchParams(params);
    next.delete("unit");
    setParams(next, { replace: true });
  };

  // Persist the view URL-first (shareable/bookmarkable) AND to localStorage
  // (survives plain nav to /tickets). Default `list` keeps the param clean.
  const setView = (value: TicketsView) => {
    const next = new URLSearchParams(params);
    if (value === "list") next.delete("view");
    else next.set("view", value);
    setParams(next, { replace: true });
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, value);
    } catch {
      /* storage unavailable — the URL still carries the view */
    }
  };

  // The scoped unit's title (from the first row) — only known once loaded.
  const scopedTitle =
    unitScoped && tickets.data && tickets.data.length > 0
      ? (tickets.data[0].unit_title ?? null)
      : null;

  return (
    <div
      className={`page${view === "board" ? " page--board" : ""}`}
      data-testid="tickets-page"
    >
      <div className="page__head">
        <div>
          <h1 className="page__title">Tickets</h1>
          <p className="page__sub">
            Every review comment across this workspace's documents, grouped by
            article — your work queue.
          </p>
        </div>
        <div className="kanban-view-toggle" data-testid="kanban-view-toggle">
          <SegmentedControl
            ariaLabel="Switch ticket view"
            value={view}
            options={VIEW_SEGMENTS}
            segmentTestIdPrefix="filter"
            onChange={setView}
          />
        </div>
      </div>

      {unitScoped ? (
        <div className="tickets__scope" data-testid="tickets-scope">
          <span className="tickets__scope-label">
            Scoped to <strong>{scopedTitle ?? `article #${unit}`}</strong>
          </span>
          <button
            type="button"
            className="tickets__scope-clear"
            onClick={clearUnit}
            data-testid="tickets-scope-clear"
          >
            <X size={13} strokeWidth={1.75} aria-hidden />
            Clear
          </button>
        </div>
      ) : null}

      <div className="tickets__filters" data-testid="tickets-filters">
        <div className="tickets__filter-row">
          <span className="tickets__filter-label">Type</span>
          <SegmentedControl
            ariaLabel="Filter tickets by type"
            value={type}
            options={TYPE_SEGMENTS}
            segmentTestIdPrefix="filter"
            onChange={(v) => setFilter("type", v)}
          />
        </div>
        {view === "list" ? (
          <div className="tickets__filter-row">
            <span className="tickets__filter-label">Status</span>
            <SegmentedControl
              ariaLabel="Filter tickets by status"
              value={status}
              options={STATUS_SEGMENTS}
              segmentTestIdPrefix="filter"
              onChange={(v) => setFilter("status", v)}
            />
          </div>
        ) : null}
      </div>

      {tickets.loading ? (
        <div className="tickets">
          <SkeletonRows rows={4} />
        </div>
      ) : tickets.error ? (
        <ErrorState body="We couldn't load the review queue." />
      ) : tickets.data && tickets.data.length > 0 ? (
        view === "board" ? (
          <KanbanBoard slug={slug} tickets={tickets.data} />
        ) : (
          <TicketsTable slug={slug} tickets={tickets.data} />
        )
      ) : filtered ? (
        <EmptyState
          icon={<ListChecks size={30} strokeWidth={1.5} />}
          title="No tickets match these filters"
          body="Try widening the type or status filter, or clear the article scope."
        />
      ) : (
        <EmptyState
          icon={<ListChecks size={30} strokeWidth={1.5} />}
          title="No tickets yet"
          body="Open an article and select text to leave the first comment."
        />
      )}
    </div>
  );
}

/**
 * The article-unit-grouped ticket grid. Presentational + props-only (no data
 * fetching), so it renders in a unit test with just a router context. Exported
 * for that test. Loose-doc tickets (no `article_unit_id`) render exactly as
 * before under the "Other documents" trailer, each linking to `/doc/:id?c=<id>`.
 */
export function TicketsTable({
  slug,
  tickets,
}: {
  slug: string;
  tickets: Ticket[];
}) {
  const groups = groupByUnit(tickets);
  return (
    <div className="tickets__groups" data-testid="tickets-list">
      {groups.map((g) => (
        <TicketGroup key={g.unitId ?? "loose"} slug={slug} group={g} />
      ))}
    </div>
  );
}

function TicketGroup({ slug, group }: { slug: string; group: UnitGroup }) {
  const openCount = group.tickets.filter((t) => t.status === "open").length;
  const total = group.tickets.length;
  return (
    <section
      className="tgroup"
      data-testid={`ticket-group-${group.unitId ?? "loose"}`}
    >
      <header className="tgroup__head">
        <div className="tgroup__title">
          {group.unitId != null ? (
            <Link
              to={`/w/${slug}/article/${group.unitId}`}
              className="tgroup__titlelink"
              data-testid={`ticket-group-link-${group.unitId}`}
            >
              {group.title}
            </Link>
          ) : (
            <span>{group.title}</span>
          )}
          <span className="tgroup__count">
            {total} ticket{total === 1 ? "" : "s"}
            {openCount > 0 ? ` · ${openCount} open` : ""}
          </span>
        </div>
        {group.unitId != null ? (
          <Link
            to={`/w/${slug}/tickets?unit=${group.unitId}`}
            className="tgroup__focus"
            data-testid={`ticket-group-focus-${group.unitId}`}
          >
            Focus queue
          </Link>
        ) : null}
      </header>

      <div className="tickets">
        {group.tickets.map((t) => (
          <TicketRow key={t.id} slug={slug} ticket={t} />
        ))}
      </div>
    </section>
  );
}

function TicketRow({ slug, ticket }: { slug: string; ticket: Ticket }) {
  // Struck through when the quote is NOT in the document — which is true of a
  // comment that lost its anchor AND of one that never had it.
  const orphaned =
    ticket.anchor_state === "orphaned" || ticket.anchor_state === "unanchored";
  const unitScoped = ticket.article_unit_id != null;
  const role = ticket.resource_role || "article";
  // Comment bodies are stored as docz-fragment JSON (or legacy markdown);
  // flatten to readable text so the row never dumps the raw serialization.
  const bodyPreview = commentBodyPreview(ticket.body);
  // Unit-scoped tickets drill into the workbench pane; loose/legacy docs keep
  // today's document-canvas deep link. Both carry the CANONICAL citation param
  // `?comment=<id>` (the same scheme the copy-link permalink emits), so the
  // workbench rail scrolls to + fade-highlights the note, and the doc canvas
  // scrolls to + blooms its anchored span.
  const to = unitScoped
    ? `/w/${slug}/article/${ticket.article_unit_id}?show=${role}&${CITATION_PARAM}=${ticket.id}`
    : buildCitationPath(slug, String(ticket.document_id), ticket.id);
  return (
    <Link to={to} className="tRow" data-testid={`ticket-row-${ticket.id}`}>
      <span
        className={`badge badge--type-${ticket.type}`}
        data-testid={`ticket-type-${ticket.id}`}
      >
        <span className="tRow__typeicon" aria-hidden>
          {ticketTypeIcon(ticket.type)}
        </span>
        {typeLabel(ticket.type)}
      </span>

      <span className="tRow__quotewrap">
        <span
          className={`tRow__quote${orphaned ? " tRow__quote--orphaned" : ""}`}
          data-testid={`ticket-quote-${ticket.id}`}
        >
          {ticket.quote || bodyPreview}
        </span>
        {bodyPreview ? (
          <span className="tRow__body">{bodyPreview}</span>
        ) : null}
      </span>

      <span className="tRow__doc" title={ticket.doc_title}>
        {unitScoped ? (
          <span
            className="role-chip role-chip--on"
            data-testid={`ticket-role-${ticket.id}`}
          >
            {roleLabel(role)}
          </span>
        ) : (
          ticket.doc_title
        )}
      </span>

      <span
        className={`badge badge--status-${ticket.status}`}
        data-testid={`ticket-status-${ticket.id}`}
      >
        <span className="badge__dot" aria-hidden />
        {statusLabel(ticket.status)}
      </span>

      <span className="tRow__anchor">
        {anchorIsFlagged(ticket.anchor_state) ? (
          <span
            className={`badge badge--anchor-${ticket.anchor_state}`}
            data-testid={`ticket-anchor-${ticket.id}`}
          >
            <AnchorGlyph state={ticket.anchor_state} />
            {anchorStateLabel(ticket.anchor_state)}
          </span>
        ) : (
          <span className="tRow__anchor-ok">Anchored</span>
        )}
      </span>

      <span className="tRow__who">
        <span className="avatar avatar--sm" aria-hidden>
          {initials(ticket.author_name)}
        </span>
        <span className="tRow__whoname">{ticket.author_name}</span>
      </span>

      <span className="tRow__age" title={ticket.created_at}>
        {formatAge(ticket.created_at)}
      </span>
    </Link>
  );
}

/** `orphaned` and `unanchored` both mean "this quote is not in the document" and
 *  share the broken-link glyph; `shifted`/`repaired` mean "it moved" and get the
 *  warning triangle. `exact` never reaches here — `anchorIsFlagged` is false for
 *  it, and the row renders the plain "Anchored" text instead. */
function AnchorGlyph({ state }: { state: AnchorState }) {
  if (state === "orphaned" || state === "unanchored")
    return <Link2 size={13} strokeWidth={1.5} aria-hidden />;
  return <AlertTriangle size={13} strokeWidth={1.5} aria-hidden />;
}
